import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

type BoostRule = {
  name: string;
  who: Array<string>;
  effect: string;
  duration: string;
};

const BOOST_RULES: Array<BoostRule> = [
  {
    name: 'LPP boost',
    who: [
      'Luxury content score above 0.77.',
      'A shown rating, which needs at least 5 reviews.',
      'Quality above 7.5, where quality = (rating × reviews + 8.27 × 50) ÷ (reviews + 50). Hotels with few reviews are pulled toward 8.27.',
      'Under 3,000 lifetime impressions and under 7 sales in the last 60 days.',
      'A hotel property type. Unique stays and rentals are out, and so is anything tagged alternate accommodation, homes and villas, entire home or vacation rental. An SPP is checked on its own property as well as its bedbank sibling.',
    ],
    effect:
      'In a place search sorted by Recommended, the 3 hotels with the highest Value that match the search are pinned to the top, with fixed boosts of 100, 80 and 60. Value is quality minus 7.5. Hotels outside the top 3 get no lift.',
    duration:
      'No end date. A hotel stays in the pool while it passes the rules, and drops out when it reaches the impression or sales cap, or its rating falls.',
  },
  {
    name: 'LPC discount',
    who: [
      'An LPC with an extranet discount whose sell period started in the last 7 days.',
      'Always-on discounts (ending in 2050 or later) and discounts that have ended do not count.',
      'Value is the best discount.',
    ],
    effect:
      'In the Recommended ranking, a flagged LPC gets a lift worth the top offer score in its region. The size of the discount does not change the lift.',
    duration:
      '7 days from the start of the discount sell period, or until the discount ends if that comes first. The Start and End columns show this window.',
  },
  {
    name: 'New LPC',
    who: [
      'An LPC in its first 7 days since its list start in the region.',
      'Value is the days left, from 7 down to 1.',
    ],
    effect: 'The same lift as an LPC discount.',
    duration:
      '7 days from the list start. It ends early once the hotel reaches 3,000 lifetime impressions or 7 sales in 60 days, which is why this group is often empty.',
  },
];

export default function HowBoostingWorks() {
  return (
    <Accordion disableGutters variant="outlined">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2">How boosting works: rules and duration</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Boost</TableCell>
                  <TableCell>Who qualifies</TableCell>
                  <TableCell>What it does in search</TableCell>
                  <TableCell>How long</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {BOOST_RULES.map((rule) => (
                  <TableRow key={rule.name} sx={{ verticalAlign: 'top' }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {rule.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {rule.who.map((line) => (
                          <Typography key={line} component="li" variant="body2">
                            {line}
                          </Typography>
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{rule.effect}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{rule.duration}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack component="ul" spacing={1} sx={{ m: 0, pl: 3 }}>
            <Typography component="li" variant="body2">
              The impression and sales caps count the hotel and its mapped bedbank and LPC offers together, so a hotel
              can reach a cap sooner than its own row suggests.
            </Typography>
            <Typography component="li" variant="body2">
              The rules are checked when a hotel is ranked. A flag can outlast its rule or window until the hotel is
              next re-ranked, and the Window column marks those as Ended.
            </Typography>
            <Typography component="li" variant="body2">
              The boosts only apply while their rollout flags are on: search_lpp_boost for the LPP picks and
              search_lpc_boost for the LPC lift. This page does not read the flags.
            </Typography>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
